import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";

interface ProtectedRouteProps {
    children: ReactNode;
}

/**
 * How often to re-check that the stored session is still usable.
 *
 * `automaticSilentRenew` only wakes up near token expiry, so a session ended server-side (admin
 * block, password change, revoked grant) can otherwise sit unnoticed on an idle tab until the next
 * API call fails with a 401.
 */
const SESSION_CHECK_INTERVAL_MS = 60_000;

/**
 * Loop guard for automatic re-authentication, in sessionStorage rather than a ref because the
 * recovery itself is a full-page navigation — a ref would reset on every attempt and never catch a
 * loop. If sign-in fails again within this window we stop retrying and show the error instead.
 */
const RECOVERY_MARKER = "athavita.auth.recoveryAt";
const RECOVERY_COOLDOWN_MS = 15_000;

function claimRecoveryAttempt(): boolean {
    try {
        const last = Number(window.sessionStorage.getItem(RECOVERY_MARKER) ?? 0);
        if (Number.isFinite(last) && Date.now() - last < RECOVERY_COOLDOWN_MS) return false;
        window.sessionStorage.setItem(RECOVERY_MARKER, String(Date.now()));
        return true;
    } catch {
        // Private mode / storage disabled — better to attempt the redirect than to dead-end.
        return true;
    }
}

function clearRecoveryMarker() {
    try {
        window.sessionStorage.removeItem(RECOVERY_MARKER);
    } catch {
        /* nothing to clear */
    }
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
    const auth = useAuth();
    const [recoveryFailed, setRecoveryFailed] = useState(false);

    const redirectToSignIn = useCallback(() => {
        if (auth.activeNavigator) return;
        if (!claimRecoveryAttempt()) {
            setRecoveryFailed(true);
            return;
        }
        void auth.signinRedirect().catch(() => setRecoveryFailed(true));
    }, [auth]);

    // Once we're genuinely signed in, forget any earlier recovery attempt so a later expiry can
    // recover again rather than being suppressed by a stale cooldown.
    useEffect(() => {
        if (auth.isAuthenticated) {
            clearRecoveryMarker();
            setRecoveryFailed(false);
        }
    }, [auth.isAuthenticated]);

    useEffect(() => {
        if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error) {
            redirectToSignIn();
        }
    }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, redirectToSignIn]);

    // An expired or revoked session surfaces as `auth.error` (silent renew fails once the refresh
    // token is gone). That used to render a red "Sign-in error" dead-end the user had to escape by
    // hand — send them back through sign-in instead, which is what they'd have to do anyway.
    useEffect(() => {
        if (auth.error && !recoveryFailed) redirectToSignIn();
        // Intentionally not depending on `recoveryFailed`: this should run when an error appears,
        // not re-run as a consequence of giving up on it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.error, redirectToSignIn]);

    useEffect(() => {
        if (!auth.isAuthenticated) return;
        const id = window.setInterval(() => {
            if (auth.user?.expired) redirectToSignIn();
        }, SESSION_CHECK_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [auth.isAuthenticated, auth.user, redirectToSignIn]);

    // Only reached when automatic recovery itself failed (e.g. the Identity Service is down) —
    // otherwise an error is transparently turned back into a sign-in.
    if (auth.error && recoveryFailed) {
        return (
            <div className="flex min-h-screen items-center justify-center p-8">
                <div className="text-center">
                    <h1 className="text-lg font-semibold text-destructive">Sign-in error</h1>
                    <p className="mt-2 text-sm text-muted-foreground">{auth.error.message}</p>
                    <button
                        type="button"
                        onClick={() => {
                            clearRecoveryMarker();
                            setRecoveryFailed(false);
                            void auth.signinRedirect();
                        }}
                        className="mt-4 text-sm font-semibold text-primary hover:underline"
                    >
                        Try signing in again
                    </button>
                </div>
            </div>
        );
    }

    if (auth.isLoading || !auth.isAuthenticated) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <p className="text-sm text-muted-foreground">Redirecting to sign-in…</p>
            </div>
        );
    }

    return <>{children}</>;
}
